
import { SimulationConfig, Ball, EscapeRing, MorphData, Vector2D } from '../../types';
import { EscapeService } from '../../services/EscapeService';
import { isInsideCollider } from '../physics/BoundaryCollision';
import { handleBallCollision } from './escape/EscapeCollision';
import { drawEscapeRings } from '../../render/EscapeRenderer';

export const EscapeLogic = {
  updateRings: (
    ring: EscapeRing,
    effectiveCfg: SimulationConfig,
    currentTS: number,
    idx: number,
    isRotationStopped: boolean,
    hyperSpeedActive: boolean,
    remainingRingsCount: number,
    minRadiusOverride?: number
  ) => {
    const rotationActive = !isRotationStopped && effectiveCfg.escapeRotationMode !== 'NONE';
    
    // STOP SHRINK LOGIC
    const shouldShrink = effectiveCfg.escapeShrinkMode && !effectiveCfg.escapeStaticMode && !(effectiveCfg.enableHyperSpeed && effectiveCfg.hyperSpeedStopShrink && hyperSpeedActive);
    
    if (shouldShrink) {
      let shrinkRate = effectiveCfg.escapeShrinkRate;
      
      if (effectiveCfg.escapeDynamicShrink) {
          // Dynamic Shrink: Increase shrink rate as more rings are destroyed
          const progress = (effectiveCfg.escapeRingCount - remainingRingsCount) / Math.max(1, effectiveCfg.escapeRingCount);
          // Multiplier goes from 1.0 to 4.0 based on progress
          shrinkRate *= (1 + progress * 3);
      }

      const baseShrink = shrinkRate * currentTS;
      const minRadiusForRing = Math.max(minRadiusOverride || 0, effectiveCfg.escapeMinRadius + idx * effectiveCfg.escapeRingSpacing);
      ring.radius = Math.max(minRadiusForRing, ring.radius - baseShrink);
    }
    
    if (rotationActive) {
      let dir = (effectiveCfg.escapeRotationMode === 'ALTERNATING') ? (idx % 2 === 0 ? 1 : -1) : (effectiveCfg.escapeRotationMode === 'COUNTER_CLOCKWISE' ? -1 : 1);
      ring.angle += effectiveCfg.escapeRotationSpeed * dir * currentTS * 0.01;
    }
  },

  handleHyperSpeed: (
    balls: Ball[],
    rings: EscapeRing[],
    effectiveCfg: SimulationConfig,
    hyperSpeedState: { active: boolean, startTime: number },
    now: number
  ) => {
    if (!effectiveCfg.enableHyperSpeed) return;

    // Detect start of hyper speed run
    if (!hyperSpeedState.active && rings.length < effectiveCfg.escapeRingCount * (effectiveCfg.hyperSpeedTriggerThreshold || 0.8)) {
         hyperSpeedState.active = true;
         hyperSpeedState.startTime = now;
    }
    // Reset if full or empty
    if (rings.length === effectiveCfg.escapeRingCount || rings.length === 0) {
        hyperSpeedState.active = false;
    }
    
    // Target Time Acceleration Logic
    if (hyperSpeedState.active && effectiveCfg.hyperSpeedTargetTime > 0) {
         const elapsed = (now - hyperSpeedState.startTime) / 1000;
         const totalTime = effectiveCfg.hyperSpeedTargetTime;
         
         if (elapsed < totalTime) {
             const remainingRings = rings.length;
             const dist = remainingRings * effectiveCfg.escapeRingSpacing;
             const timeRem = totalTime - elapsed;
             
             const b = balls[0];
             if (b) {
                 const currentSpeed = Math.sqrt(b.vel.x*b.vel.x + b.vel.y*b.vel.y);
                 const progress = elapsed / totalTime;
                 // Gradual acceleration based on hyperSpeedAcceleration
                 const boost = 1 + (progress * progress * (effectiveCfg.hyperSpeedAcceleration || 0.1) * 100); 
                 const minSpeed = (dist / timeRem) * 0.016; 
                 
                 if (currentSpeed < minSpeed || progress > 0.1) {
                     const target = Math.max(currentSpeed, minSpeed * boost);
                     const finalSpeed = Math.min(target, 5000); 
                     if (finalSpeed > currentSpeed && currentSpeed > 0.001) {
                         b.vel.x = (b.vel.x / currentSpeed) * finalSpeed;
                         b.vel.y = (b.vel.y / currentSpeed) * finalSpeed;
                     }
                 }
             }
         }
    }
  },

  handleBallCollision,

  init: (
    effectiveCfg: SimulationConfig,
    centerX: number,
    centerY: number,
    escapeRingsRef: React.MutableRefObject<EscapeRing[]>,
    initialEscapeRingCountRef: React.MutableRefObject<number>,
    timeRemainingRef: React.MutableRefObject<number>,
    escapeAccelerationBoostRef: React.MutableRefObject<number>,
    createBall: (overrides: Partial<Ball>) => Ball,
    getInitialBallState: (index: number, total: number, config?: any) => { pos: { x: number, y: number }, vel: { x: number, y: number } }
  ): Ball[] => {
    timeRemainingRef.current = effectiveCfg.escapeTimerStart || 10;
    escapeAccelerationBoostRef.current = 0;
    escapeRingsRef.current = EscapeService.generateRings(effectiveCfg);
    
    if (effectiveCfg.outsideColliderEnabled && effectiveCfg.outsideColliderCulling) {
        escapeRingsRef.current = escapeRingsRef.current.filter(ring => 
            isInsideCollider(0, 0, ring.radius, effectiveCfg, effectiveCfg.outsideColliderCullingMargin)
        );
    }
    
    initialEscapeRingCountRef.current = escapeRingsRef.current.length; 
    const total = Math.max(1, effectiveCfg.initialBalls || 1);
    
    return Array.from({ length: total }).map((_, i) => {
        const individualConfig = effectiveCfg.ballPlacementIndividualEnabled && effectiveCfg.ballPlacementConfigs && effectiveCfg.ballPlacementConfigs[i] 
            ? effectiveCfg.ballPlacementConfigs[i] 
            : null;
        const { pos, vel } = getInitialBallState(i, total, individualConfig);
        return createBall({
            id: `escape-ball-${i}`,
            radius: effectiveCfg.ballRadius,
            color: individualConfig?.color || (i === 0 ? effectiveCfg.ball1Color : (i === 1 ? effectiveCfg.ball2Color : '#ffffff')),
            pos,
            vel
        });
    });
  },

  render: (
    ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    escapeRings: EscapeRing[],
    config: SimulationConfig,
    globalHue: number,
    morphData: MorphData | undefined,
    webglRenderer: any,
    cam: any,
    initialEscapeRingCount: number,
    timeRemaining: number,
    balls: Ball[],
    counterPop: number,
    zoomLevel: number = 1.0
  ) => {
    const width = ctx.canvas.width / (window.devicePixelRatio || 1);
    const height = ctx.canvas.height / (window.devicePixelRatio || 1);

    const morphDataSimple = morphData ? {
        from: morphData.from,
        to: morphData.to,
        factor: morphData.factor
    } : {
        from: config.containerShape,
        to: config.containerShape,
        factor: 0
    };
    
    // Hyper Speed Render Limit
    let ringsToRender = escapeRings;
    if (config.enableHyperSpeed && config.hyperSpeedRenderLimit && escapeRings.length > config.hyperSpeedRenderLimit) {
        ringsToRender = escapeRings.slice(0, config.hyperSpeedRenderLimit);
    }

    if (config.outsideColliderEnabled && config.outsideColliderCulling) {
        ringsToRender = ringsToRender.filter(ring => 
            isInsideCollider(0, 0, ring.radius, config, config.outsideColliderCullingMargin)
        );
    }

    if (webglRenderer) {
        webglRenderer.renderRings(ringsToRender, config, cam, morphDataSimple, globalHue, zoomLevel);
        if (config.escapeSideGuards) {
            drawEscapeRings(ctx, centerX, centerY, ringsToRender, config, globalHue, morphData, true, cam, width, height);
        }
    } else {
        drawEscapeRings(ctx, centerX, centerY, ringsToRender, config, globalHue, morphData, false, cam, width, height);
    }
  }
};
